<script setup lang="ts">
import { userModule } from '@/store/user/module'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { subscriptionModule } from '@/store/subscription/module'
import { giftModule } from '@/store/gift/module'
import { toastModule } from '@/store/toast/module'
import * as Sentry from '@sentry/browser'

const {
    ModalContainer,
    Button: PocketButton,
    Errors,
} = UIKit

const router = useRouter()

const isLoading = ref(true)
const giftSubEndDate = ref('')
const isProcessingRedeemGift = ref(false)
const activateGiftError = ref('')
// Capture subscriptions before gift redemption, to avoid changing modal
// state after clicking "Redeem Now"
const initialSubscriptions = ref<typeof subscriptionModule.state.subscriptions>([])

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const validGift = computed(() => {
    return giftModule.getters.getValidGift()
})

const hasUnredeemableGiftLink = computed(() => {
    return !validGift.value?.isRedeemable
})

const giftTier = computed(() => {
    return giftModule.getters.getGiftTier()
})

const giftTierDuration = computed(() => {
    return giftTier.value === 'gift-1-month'
        ? 1
        : giftTier.value === 'gift-3-month'
            ? 3
            : 0
})

const giftError = computed(() => {
    return giftModule.state.giftError
})

const activeRenewingSubscriptions = computed(() => {
    return initialSubscriptions.value.filter(sub => {
        const expiresAt = new Date(sub.expiresAt.iso)
        const isExpired = new Date().getTime() > expiresAt.getTime()
        return sub.willAutoRenew && !isExpired
    })
})

const activeExpiringSubscriptions = computed(() => {
    return initialSubscriptions.value.filter(sub => {
        const expiresAt = new Date(sub.expiresAt.iso)
        const isExpired = new Date().getTime() > expiresAt.getTime()
        const expiringPremiumSources = [ 'Apple', 'Google', 'Stripe', 'Support', 'Gift' ]
        return !sub.willAutoRenew && !isExpired && sub.source && expiringPremiumSources.includes(sub.source)
    })
})

const soonestRenewingSubscription = computed(() => {
    return [ ...activeRenewingSubscriptions.value ]
        .sort((a, b) => new Date(a.expiresAt.iso).getTime() - new Date(b.expiresAt.iso).getTime())[0]
})

const editableSubscription = computed(() => {
    const sub = soonestRenewingSubscription.value
    return sub && 'bundle' in sub && sub.source === 'Stripe' ? sub : undefined
})

const latestExpiringSubscription = computed(() => {
    return [ ...activeExpiringSubscriptions.value ]
        .sort((a, b) => new Date(b.expiresAt.iso).getTime() - new Date(a.expiresAt.iso).getTime())[0]
})

const subscriptionType = computed(() => {
    if (soonestRenewingSubscription.value) {
        return 'renewing'
    }
    return latestExpiringSubscription.value ? 'expiring' : null
})

const subscriptionRenewalDate = computed(() => {
    const sub = soonestRenewingSubscription.value
    return sub ? formatDate(sub.expiresAt.iso) : ''
})

const subscriptionExpirationDate = computed(() => {
    const sub = latestExpiringSubscription.value
    return sub ? formatDate(sub.expiresAt.iso) : ''
})

const subscriptionExpirationDateShort = computed(() => {
    const sub = latestExpiringSubscription.value
    return sub ? formatDate(sub.expiresAt.iso, { withYear: false }) : ''
})

const title = computed(() => {
    if (hasUnredeemableGiftLink.value) {
        return 'Invalid Gift Link'
    }

    return !subscriptionType.value
        ? `You have a ${giftTierDuration.value}-month gift to redeem!`
        : `Do you want to redeem your ${giftTierDuration.value}-month gift now?`
})

const titleRef = ref<HTMLElement>()

onMounted(async () => {
    await userModule.actions.fetchUserData()

    initialSubscriptions.value = subscriptionModule.state.subscriptions
    giftSubEndDate.value = calculateGiftSubEndDate()
    isLoading.value = false

    nextTick(() => {
        titleRef.value?.focus()
    })
})

const formatDate = (iso: string, { withYear = true } = {}) => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
    if (withYear) {
        options.year = 'numeric'
    }
    return new Date(iso).toLocaleDateString('en-US', options)
}

const calculateGiftSubEndDate = () => {
    const date = new Date()
    const initialDate = date.getDate()
    date.setMonth(date.getMonth() + giftTierDuration.value)
    if (date.getDate() !== initialDate) {
        date.setDate(0)
    }
    return formatDate(date.toISOString())
}

const closeRedeemGiftModal = () => {
    giftModule.actions.closeRedeemGiftModal()
}

const redeemGift = async () => {
    if (isProcessingRedeemGift.value) {
        return
    }

    isProcessingRedeemGift.value = true

    try {
        await giftModule.actions.activateGift()
        giftModule.actions.closeRedeemGiftModal()
        const subtext = subscriptionType.value === 'renewing'
            ? 'Make sure to cancel your existing subscription to avoid extra charges (Settings > Purchases > Edit)'
            : undefined
        toastModule.actions.displayToast({ title: 'Gift Redeemed!', subtext })
    } catch (err) {
        Sentry.captureException(err)
        activateGiftError.value =
            'Unable to redeem gift. Close the window and try your link again. If the error persists, contact support.'
    }

    isProcessingRedeemGift.value = false
}

const editSubscription = async () => {
    if (subscriptionType.value !== 'renewing') {
        return
    }
    giftModule.actions.dismissRedeemGiftModal()

    const subscriptionId = editableSubscription.value?.objectId

    await router.push({
        name: 'purchases',
        ...(subscriptionId ? { query: { subscriptionId } } : {}),
    })
}

</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="redeemGiftModal"
            :show-close-button="true"
            :is-dark-mode="isDarkMode"
            @close="closeRedeemGiftModal"
        >
            <div
                v-if="!isLoading"
                class="redeem-gift-modal"
            >
                <h1
                    ref="titleRef"
                    class="redeem-gift-modal__title"
                    tabindex="-1"
                >
                    {{ title }}
                </h1>
                <template v-if="hasUnredeemableGiftLink">
                    <div
                        class="redeem-gift-modal__unredeemable-gift-description"
                        v-dark
                    >
                        We were unable to redeem your gift.
                    </div>
                    <div
                        class="redeem-gift-modal__unredeemable-gift-suggestion"
                        v-dark
                    >
                        Wait a few minutes, then try the link again. If the problem persists, contact support.
                    </div>
                </template>
                <template v-else>
                    <div
                        v-if="!subscriptionType"
                        class="redeem-gift-modal__start-date"
                        v-dark
                    >
                        Your Premium access will start now and end on
                        <strong>{{ giftSubEndDate }}</strong>.
                    </div>
                    <div
                        v-else
                        class="redeem-gift-modal__start-date"
                        v-dark
                    >
                        You have an active Premium subscription that
                        {{ subscriptionType === 'renewing' ? 'renews' : 'expires' }} on
                        <strong>{{
                            subscriptionType === 'renewing'
                                ? subscriptionRenewalDate : subscriptionExpirationDate }}</strong>.
                    </div>
                    <div
                        v-if="subscriptionType === 'renewing'"
                        class="redeem-gift-modal__subscription-warning"
                        v-dark
                    >
                        A gift will not replace or remove your subscription —
                        you must cancel it in order to avoid unwanted charges.
                    </div>
                    <div
                        v-else-if="subscriptionType === 'expiring'"
                        class="redeem-gift-modal__subscription-warning"
                        v-dark
                    >
                        To maximize your Premium access,
                        redeem your gift after {{ subscriptionExpirationDateShort }}.
                    </div>
                    <div
                        v-else
                        class="redeem-gift-modal__redeem-later"
                        v-dark
                    >
                        (It’s also fine to wait — use the same gift link to start your access at a later date.)
                    </div>
                    <Errors
                        v-if="activateGiftError"
                        class="redeem-gift-modal__error-banner"
                        :errors="[ activateGiftError ]"
                        :isDarkMode="isDarkMode"
                    />
                </template>
                <div
                    v-if="hasUnredeemableGiftLink || activateGiftError || giftError"
                    class="redeem-gift-modal__actions"
                >
                    <PocketButton
                        class="redeem-gift-modal__close-btn"
                        :isDarkMode="isDarkMode"
                        @click="closeRedeemGiftModal"
                        @keydown.enter.stop.prevent="closeRedeemGiftModal"
                    >
                        Close
                    </PocketButton>
                </div>
                <div
                    v-else-if="subscriptionType === 'renewing'"
                    class="redeem-gift-modal__actions"
                >
                    <PocketButton
                        class="redeem-gift-modal__redeem-gift-btn"
                        type="secondary"
                        :isDarkMode="isDarkMode"
                        :is-loading="isProcessingRedeemGift"
                        @click="redeemGift"
                        @keydown.enter.stop.prevent="redeemGift"
                    >
                        Redeem Now
                    </PocketButton>
                    <PocketButton
                        class="redeem-gift-modal__edit-subscription-btn"
                        :isDarkMode="isDarkMode"
                        :disabled="isProcessingRedeemGift"
                        @click="editSubscription"
                        @keydown.enter.stop.prevent="editSubscription"
                    >
                        {{ editableSubscription ? 'Edit Subscription' : 'Manage Purchases' }}
                    </PocketButton>
                </div>
                <div
                    v-else
                    class="redeem-gift-modal__actions"
                >
                    <PocketButton
                        class="redeem-gift-modal__redeem-later-btn"
                        type="secondary"
                        :isDarkMode="isDarkMode"
                        :disabled="isProcessingRedeemGift"
                        @click="closeRedeemGiftModal"
                        @keydown.enter.stop.prevent="closeRedeemGiftModal"
                    >
                        Redeem Later
                    </PocketButton>
                    <PocketButton
                        class="redeem-gift-modal__redeem-gift-btn"
                        :isDarkMode="isDarkMode"
                        :is-loading="isProcessingRedeemGift"
                        @click="redeemGift"
                        @keydown.enter.stop.prevent="redeemGift"
                    >
                        Redeem Now
                    </PocketButton>
                </div>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.redeem-gift-modal {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 36px 40px 24px;

    &__title {
        width: 265px;
        font-size: 18px;
        font-weight: 600;
        line-height: 25px;
        letter-spacing: 0.3px;
        text-align: center;
        margin-bottom: 12px;

        &:focus {
            outline: none;
        }
    }

    &__start-date,
    &__redeem-later,
    &__subscription-warning,
    &__unredeemable-gift-description,
    &__unredeemable-gift-suggestion {
        color: $ash;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;
        text-align: center;
        width: 294px;

        &--dark {
            color: $white;
        }
    }

    &__start-date,
    &__unredeemable-gift-description {
        margin-bottom: 20px;
    }

    &__redeem-later,
    &__subscription-warning,
    &__unredeemable-gift-suggestion {
        margin-bottom: 32px;
    }

    &__error-banner {
        width: 294px;
        margin-bottom: 24px;
    }

    &__actions {
        display: flex;
        gap: 8px;
    }
}
</style>
